/**
 * Canonical mapping from backend error code strings to product-owned,
 * user-friendly messages. This ensures UX copy is frontend-owned and does not
 * depend on backend message strings that can change silently.
 *
 * Backend codes are emitted in the `detail.code` field of 4xx/5xx responses.
 */
export const ERROR_CODE_MESSAGES: Record<string, string> = {
  // Authentication & session
  SESSION_EXPIRED: "Your session has expired. Please sign in again.",
  INVALID_CREDENTIALS: "Incorrect email or password.",
  ACCOUNT_LOCKED:
    "Your account is temporarily locked. Please try again in 15 minutes or contact support.",
  OTP_REQUIRED: "A verification code is required to continue.",
  EMAIL_VERIFICATION_REQUIRED:
    "Please verify your email address before continuing.",

  // OTP / verification
  OTP_EXPIRED:
    "Your verification code has expired. Please request a new one.",
  OTP_INVALID:
    "That code is incorrect. Please check and try again.",
  OTP_MAX_ATTEMPTS:
    "Too many incorrect attempts. Please request a new verification code.",

  // Registration
  EMAIL_ALREADY_EXISTS:
    "This email is already registered. Try signing in instead.",
  PHONE_ALREADY_EXISTS: "This phone number is already registered.",

  // Rate limiting
  RATE_LIMITED:
    "Too many attempts. Please wait a moment and try again.",
  TOO_MANY_REQUESTS:
    "Too many requests. Please wait a moment and try again.",

  // Permissions
  ACCESS_DENIED: "You don't have permission to perform this action.",
  PERMISSION_DENIED: "You don't have permission to perform this action.",
  NOT_AUTHORIZED: "Please sign in to continue.",
  VETTING_REQUIRED:
    "Your account is pending approval. This will be available once our team has vetted your account.",

  // Resources
  NOT_FOUND: "The requested resource could not be found.",
  COMPANY_NOT_FOUND: "Company not found.",
  USER_NOT_FOUND: "User not found.",

  // Payments (stub � extend when billing is enabled)
  PAYMENT_FAILED:
    "Your payment couldn't be completed. No money has been deducted. Please try again.",
  SUBSCRIPTION_REQUIRED:
    "This feature requires an active subscription.",

  // Generic server errors
  INTERNAL_ERROR:
    "Something went wrong on our end. Please try again in a moment.",
  SERVICE_UNAVAILABLE:
    "This service is temporarily unavailable. Please try again shortly.",
};

/**
 * Returns a user-friendly message for a backend error code, or null if the
 * code is not in the catalog. Callers should fall back to extractErrorMessage.
 */
export function getMessageForCode(code: string | number | undefined): string | null {
  if (!code) return null;
  return ERROR_CODE_MESSAGES[String(code)] ?? null;
}
